import { getChatSession } from './getChatSession'
import { saveChatUserToSession } from './saveChatUserToSession'
import { convertLinksToAnchors } from './convertLinksToAnchors'

/**
 * Renders a chat message
 * @param {ChatSessionMessage} message
 * @param {Object} [options] - Optional parameters for message rendering
 * @param {string} [options.avatar] - Avatar URL for agent messages
 * @param {boolean} [options.showUserAvatar] - Whether to show user avatar SVG (default: false)
 * @param {string} [options.customerLetterColor] - Color for customer avatar letter (default: '#63CE4D')
 * @param {string} [options.customerBackgroundColor] - Background color for customer avatar (default: '#002244')
 */
export function renderMessage(message, options = {}) {
  const {
    avatar,
    showUserAvatar = false,
    customerLetterColor = getComputedStyle(document.documentElement)
      .getPropertyValue('--brand-profile-text-color')
      .trim() || '#63CE4D',
    customerBackgroundColor = getComputedStyle(document.documentElement)
      .getPropertyValue('--brand-profile-background')
      .trim() || '#002244',
  } = options

  const session = getChatSession()

  const chat = document.getElementById('chat')
  if (!chat) {
    throw Error('unexpected: chat missing')
  }

  const containerDiv = document.createElement('div')
  containerDiv.classList.add('message-container')
  if (message.authorType === 'agent') {
    containerDiv.classList.add('agent')
    const agent = session.users.find((u) => u.id === message.authorId)
    if (agent) {
      saveChatUserToSession(agent)
    }

    // Add agent avatar if provided
    if (avatar) {
      const avatarImage = document.createElement('img')
      avatarImage.src = avatar
      avatarImage.classList.add('agent-avatar')
      avatarImage.alt = 'agent-avatar'
      avatarImage.width = 38
      avatarImage.height = 38
      containerDiv.appendChild(avatarImage)
    }
  } else if (message.authorType === 'user') {
    containerDiv.classList.add('user')

    // Add user avatar SVG if enabled
    if (showUserAvatar) {
      const svgNS = 'http://www.w3.org/2000/svg'
      const customerAvatar = document.createElementNS(svgNS, 'svg')

      customerAvatar.setAttribute('viewBox', '0 0 42 42')
      customerAvatar.setAttribute('role', 'img')
      customerAvatar.setAttribute('aria-label', 'Customer avatar')
      customerAvatar.classList.add('customer-avatar')

      // Inline the SVG so we can adjust the properties
      customerAvatar.innerHTML = `
        <circle cx="21" cy="21" r="20"
                fill="${customerBackgroundColor}" stroke="${customerLetterColor}" stroke-width="2"/>
        <path d="M12.988 25V22.012L10.168 16.6H11.908L13.768 20.476L15.616 16.6H17.332L14.524 22.012V25H12.988ZM20.0696 25.144C19.4936 25.144 18.9736 25.012 18.5096 24.748C18.0536 24.484 17.6896 24.12 17.4176 23.656C17.1536 23.184 17.0216 22.64 17.0216 22.024C17.0216 21.408 17.1576 20.868 17.4296 20.404C17.7016 19.932 18.0656 19.564 18.5216 19.3C18.9856 19.036 19.5056 18.904 20.0816 18.904C20.6496 18.904 21.1616 19.036 21.6176 19.3C22.0816 19.564 22.4456 19.932 22.7096 20.404C22.9816 20.868 23.1176 21.408 23.1176 22.024C23.1176 22.64 22.9816 23.184 22.7096 23.656C22.4456 24.12 22.0816 24.484 21.6176 24.748C21.1536 25.012 20.6376 25.144 20.0696 25.144ZM20.0696 23.812C20.4696 23.812 20.8176 23.664 21.1136 23.368C21.4096 23.064 21.5576 22.616 21.5576 22.024C21.5576 21.432 21.4096 20.988 21.1136 20.692C20.8176 20.388 20.4736 20.236 20.0816 20.236C19.6736 20.236 19.3216 20.388 19.0256 20.692C18.7376 20.988 18.5936 21.432 18.5936 22.024C18.5936 22.616 18.7376 23.064 19.0256 23.368C19.3216 23.664 19.6696 23.812 20.0696 23.812ZM26.7229 25.144C25.9789 25.144 25.4029 24.912 24.9949 24.448C24.5949 23.984 24.3949 23.304 24.3949 22.408V19.048H25.9189V22.264C25.9189 22.776 26.0229 23.168 26.2309 23.44C26.4389 23.712 26.7669 23.848 27.2149 23.848C27.6389 23.848 27.9869 23.696 28.2589 23.392C28.5389 23.088 28.6789 22.664 28.6789 22.12V19.048H30.2149V25H28.8589L28.7389 23.992C28.5549 24.344 28.2869 24.624 27.9349 24.832C27.5909 25.04 27.1869 25.144 26.7229 25.144Z" fill="${customerLetterColor}"/>
      `

      containerDiv.appendChild(customerAvatar)
    }
  } else {
    containerDiv.classList.add('system')
  }

  const messageDiv = document.createElement('div')
  messageDiv.className = 'message'

  // Convert links to clickable anchors before setting innerHTML
  const processedText = convertLinksToAnchors(message.text)
  messageDiv.innerHTML = `<div>${processedText}</div>`

  containerDiv.appendChild(messageDiv)
  chat.appendChild(containerDiv)
}
